"use client";
import { MouseEventHandler, useState } from "react";
import { cn } from "@shared/src/utils/cn";
import { IoCopy } from "react-icons/io5";
import { shortenAddress } from "@shared/src/utils/helpers";
import { ixToast } from "@shared/src/utils/ixToast";

interface CopyButtonProps {
  text: string;
  showToast?: boolean;
  toastMessage?: string;
  iconClassName?: string;
  className?: string;
}

export function CopyButton({
  text,
  showToast = true,
  toastMessage = "Copied to clipboard",
  iconClassName,
  className,
}: CopyButtonProps) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy: MouseEventHandler<HTMLButtonElement> = e => {
    navigator.clipboard.writeText(text);
    setIsCopied(true);

    if (showToast) {
      ixToast.success(toastMessage);
    }

    e.preventDefault();

    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={cn(
        "text-text-tertiary hover:text-text-primary transition-colors",
        isCopied && "text-icons-icon-success",
        iconClassName,
        className,
      )}
      aria-label="Copy to clipboard"
    >
      <IoCopy size={10} />
    </button>
  );
}

interface TextWithCopyProps {
  text: string;
  className?: string;
  iconClassName?: string;
  showToast?: boolean;
  shorten?: boolean | number;
  toastMessage?: string;
  differentTextToCopy?: string;
}

export function TextWithCopy({
  text,
  className,
  iconClassName,
  showToast = true,
  toastMessage = "Copied to clipboard",
  shorten = true,
  differentTextToCopy,
}: TextWithCopyProps) {
  const displayText = shorten ? shortenAddress(text, typeof shorten === "number" ? shorten : undefined) : text;
  const textToCopy = differentTextToCopy ?? text;

  return (
    <div title={text} className={cn("inline-flex items-center gap-2", className)}>
      <span className="truncate">{displayText}</span>
      <CopyButton text={textToCopy} showToast={showToast} toastMessage={toastMessage} iconClassName={iconClassName} />
    </div>
  );
}
